import { httpClient } from '@bs/core'
import type {
  FindPositionRequest,
  FindPositionResponse,
  LocationDetailRaw,
  MailOrderShippingZone,
  PreviewTimeSlotRequest,
  PreviewTimeSlotResponse,
  SpecialHoursLocationData,
  WarehouseZoneApi,
  WhZoneListResponse,
  ZoneAreaType,
} from '../../types/location'
import type {
  CurbsidePickupHours,
  InstorePickupHours,
  Payments,
} from '../../types/warehouse'

export interface WhZoneListParams {
  warehouse_id: string
  area_type: ZoneAreaType
  page?: number
  limit?: number
  search?: string
}

export interface LocationItem {
  id: string
  name: string
  address?: string
  active?: boolean
}

interface ApiLocationItem {
  id: number | string
  name: string
  address?: string
  active?: boolean
  is_active?: boolean
}

function mapFromApi(item: ApiLocationItem): LocationItem {
  return {
    id: String(item.id),
    name: item.name,
    address: item.address,
    active: item.active ?? item.is_active ?? true,
  }
}

const locationUrl = (storeId: string) => `/stores/${storeId}/locations`
const locationDetailUrl = (warehouseId: string) =>
  `/warehouses/location_v2/${warehouseId}`
const draftWarehouseUrl = (orgId: string) =>
  `/organizations/${orgId}/pantry-warehouses`

export interface CreateLocationDraftPayload {
  name: string
  address_1: string
  address_2: string
  city: string
  state: string
  postal_code: string
  country: string
  phone_number: string
  license: string
  location_status: 'live' | 'hidden'
}

export interface CreateLocationDraftResponse {
  warehouse_id?: string
  _id?: string
  id?: string
  warehouseId?: string
  [key: string]: unknown
}

type PickupRules = Pick<
  InstorePickupHours,
  'pickup_window' | 'next_available_time' | 'special_hours'
>

// Existing pickup objects from GET detail. A Store Hours save regenerates
// `pickup_hours` from store hours but must forward these as-is (ACD-15249
// AC10) — otherwise it wipes special hours and resets the pickup rules.
export interface UpdateHoursExisting {
  instore_pickup?: Partial<PickupRules>
  curbside_pickup_hours?: Partial<
    Pick<
      CurbsidePickupHours,
      | 'pickup_window'
      | 'next_available_time'
      | 'special_hours'
      | 'same_instore_pickup'
    >
  >
}

const DEFAULT_PICKUP_WINDOW = 30
const DEFAULT_PICKUP_NEXT_AVAILABLE = 60

const WEEK_DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const

interface StoreHoursSlot {
  from: string
  to: string
}

interface PickupHoursDay {
  from: string
  to: string
  time_slot: Array<StoreHoursSlot>
  pickup_window: number
  next_available_time: number
  time_slot_option: 'customize_individual_time_slots'
}

/**
 * Derive the per-day `pickup_hours` payload (used by both `instore_pickup` and
 * `curbside_pickup_hours`) from the store-hours payload. Mirrors the shape the
 * backend expects for pickup so editing store hours pushes the same windows
 * to in-store and curbside automatically.
 */
function pickupHoursFromStoreHours(
  storeHours: Record<string, unknown>,
  rules: { pickup_window?: number; next_available_time?: number } = {},
): Record<string, PickupHoursDay> {
  const out: Record<string, PickupHoursDay> = {}
  for (const day of WEEK_DAY_KEYS) {
    const raw = storeHours[day]
    const slots: StoreHoursSlot[] = Array.isArray(raw)
      ? (raw as StoreHoursSlot[]).filter(
          (s) => s && typeof s.from === 'string' && typeof s.to === 'string',
        )
      : []
    const first = slots[0]
    const last = slots[slots.length - 1]
    out[day] = {
      from: first?.from ?? '',
      to: last?.to ?? '',
      time_slot: slots.map((s) => ({ from: s.from, to: s.to })),
      pickup_window: rules.pickup_window ?? DEFAULT_PICKUP_WINDOW,
      next_available_time:
        rules.next_available_time ?? DEFAULT_PICKUP_NEXT_AVAILABLE,
      time_slot_option: 'customize_individual_time_slots',
    }
  }
  return out
}

export const locationApis = {
  createDraft: async (
    orgId: string,
    payload: CreateLocationDraftPayload,
  ): Promise<CreateLocationDraftResponse> => {
    return (await httpClient.json.post(
      draftWarehouseUrl(orgId),
      payload,
    )) as CreateLocationDraftResponse
  },

  getList: async (storeId: string): Promise<LocationItem[]> => {
    const raw = (await httpClient.json.get(locationUrl(storeId))) as
      | ApiLocationItem[]
      | { items: ApiLocationItem[] }
    const items = Array.isArray(raw) ? raw : (raw.items ?? [])
    return items.map(mapFromApi)
  },

  getDetail: async (
    storeId: string,
    warehouseId: string,
  ): Promise<LocationDetailRaw> => {
    return (await httpClient.json.get(locationDetailUrl(warehouseId), {
      params: { store_id: storeId },
    })) as LocationDetailRaw
  },

  updateHours: async (
    storeId: string,
    warehouseId: string,
    hours: Record<string, unknown>,
    existing: UpdateHoursExisting = {},
  ): Promise<void> => {
    const pickupObject = (
      prev: UpdateHoursExisting['curbside_pickup_hours'],
    ) => {
      const pickupWindow = prev?.pickup_window ?? DEFAULT_PICKUP_WINDOW
      const nextAvailable =
        prev?.next_available_time ?? DEFAULT_PICKUP_NEXT_AVAILABLE
      return {
        pickup_window: pickupWindow,
        next_available_time: nextAvailable,
        pickup_hours: pickupHoursFromStoreHours(hours, {
          pickup_window: pickupWindow,
          next_available_time: nextAvailable,
        }),
        special_hours: prev?.special_hours ?? [],
        // Curbside only; the serializer defaults it to false when omitted.
        ...(typeof prev?.same_instore_pickup === 'boolean'
          ? { same_instore_pickup: prev.same_instore_pickup }
          : {}),
      }
    }
    await httpClient.json.patch(locationDetailUrl(warehouseId), {
      store_id: storeId,
      location_data: {
        location_hours: hours,
        instore_pickup: pickupObject(existing.instore_pickup),
        curbside_pickup_hours: pickupObject(existing.curbside_pickup_hours),
      },
    })
  },

  updateSpecialHours: async (
    storeId: string,
    warehouseId: string,
    locationData: SpecialHoursLocationData,
  ): Promise<LocationDetailRaw | undefined> => {
    // update_location_v2 answers with the fresh location document; the hook
    // writes its special_hours into the detail cache (no stale rebuilds).
    return (await httpClient.json.patch(locationDetailUrl(warehouseId), {
      store_id: storeId,
      location_data: locationData,
    })) as LocationDetailRaw | undefined
  },

  updateDeliverySchedule: async (
    storeId: string,
    warehouseId: string,
    data: {
      canfleet_enable_delivery_hours?: boolean
      required_delivery_time?: boolean
      delivery_display_asap_option?: '' | 'asap' | 'first'
      delivery?: {
        delivery_hours: Record<string, unknown>
        delivery_window?: number
        next_available_time?: number
        [k: string]: unknown
      }
    },
  ): Promise<void> => {
    await httpClient.json.patch(locationDetailUrl(warehouseId), {
      store_id: storeId,
      location_data: data,
    })
  },

  updateMailOrder: async (
    storeId: string,
    warehouseId: string,
    data: {
      mail_order_flat_rate: string
      mail_order_minimum_order: string
      mail_order_free_shipping_over: string
    },
  ): Promise<void> => {
    await httpClient.json.patch(locationDetailUrl(warehouseId), {
      store_id: storeId,
      location_data: {
        ...data,
        tip_enabled_fulfillment_types: ['local_delivery'],
      },
    })
  },

  updateTips: async (
    storeId: string,
    warehouseId: string,
    data: {
      tip_enabled_fulfillment_types: string[]
      tip_percent_options: string[]
    },
  ): Promise<void> => {
    await httpClient.json.patch(locationDetailUrl(warehouseId), {
      store_id: storeId,
      location_data: data,
    })
  },

  updatePayments: async (
    storeId: string,
    warehouseId: string,
    payments: Payments,
  ): Promise<void> => {
    await httpClient.json.patch(locationDetailUrl(warehouseId), {
      store_id: storeId,
      location_data: { payments },
    })
  },

  updateDetails: async (
    storeId: string,
    warehouseId: string,
    data: {
      name?: string
      phone_number?: string
      address_1?: string
      address_2?: string
      city?: string
      state?: string
      country?: string
      postal_code?: string
      license?: string
      email?: string
      photo_url?: string
      area_type?: ZoneAreaType
      minimum_legal_age?: number
      shipping_tax_percentage?: string
    },
  ): Promise<void> => {
    await httpClient.json.patch(locationDetailUrl(warehouseId), {
      store_id: storeId,
      location_data: data,
    })
  },

  updateVisibility: async (
    storeId: string,
    warehouseId: string,
    visible: boolean,
  ): Promise<void> => {
    await httpClient.json.patch(locationDetailUrl(warehouseId), {
      store_id: storeId,
      location_data: {
        location_status: visible ? 'live' : 'hidden',
      },
    })
  },

  updateFulfillment: async (
    storeId: string,
    warehouseId: string,
    data: {
      local_pickup?: boolean
      curbside_pickup?: boolean
      enable_drive_through_pickup?: boolean
      enable_delivery?: boolean
      enable_mail_order?: boolean
      regular_shipping?: boolean
      mail_order_linked?: string
      delivery_linked?: string
      delivery_api_key?: string
    },
  ): Promise<void> => {
    await httpClient.json.patch(locationDetailUrl(warehouseId), {
      store_id: storeId,
      location_data: data,
    })
  },

  updateExcludedCategories: async (
    storeId: string,
    warehouseId: string,
    data: {
      excluded_local_pickup_categories?: number[]
      excluded_curbside_pickup_categories?: number[]
      excluded_local_delivery_categories?: number[]
      excluded_mail_order_categories?: number[]
    },
  ): Promise<void> => {
    await httpClient.json.patch(locationDetailUrl(warehouseId), {
      store_id: storeId,
      location_data: data,
    })
  },

  updateDeliveryProvider: async (
    storeId: string,
    warehouseId: string,
    data: { delivery_linked: string; delivery_api_key: string },
  ): Promise<void> => {
    await httpClient.json.patch(locationDetailUrl(warehouseId), {
      store_id: storeId,
      location_data: data,
    })
  },

  // Replaces the full `mail_order_zones` array on a location. The Shipping
  // Zones card calls this for every Add/Edit/Delete from the dialog — there
  // is no per-zone endpoint, so the UI keeps the whole list in sync locally
  // and PATCHes the entire array each time. Each zone carries its own
  // `excluded_categories` (numeric category ids) for per-zone mail-order
  // exclusions.
  updateMailOrderZones: async (
    storeId: string,
    warehouseId: string,
    zones: MailOrderShippingZone[],
  ): Promise<void> => {
    await httpClient.json.patch(locationDetailUrl(warehouseId), {
      store_id: storeId,
      location_data: { mail_order_zones: zones },
    })
  },

  // Single-location threshold update. Sent via the same location_v2 PATCH the
  // rest of the Location detail tabs use.
  updateLowStockThreshold: async (
    storeId: string,
    warehouseId: string,
    threshold: number,
  ): Promise<void> => {
    await httpClient.json.patch(locationDetailUrl(warehouseId), {
      store_id: storeId,
      location_data: { low_stock_threshold: threshold },
    })
  },

  // Bulk-apply the threshold to every warehouse in the store. Backend reuses
  // the per-location `PATCH /warehouses/location_v2/:id` endpoint and fans the
  // value out to every sibling warehouse when `location_data` carries the
  // `apply_low_stock_threshold_to_all_locations` field (set to the reserve
  // quantity, not a boolean).
  applyLowStockThresholdAll: async (
    storeId: string,
    warehouseId: string,
    threshold: number,
  ): Promise<void> => {
    await httpClient.json.patch(locationDetailUrl(warehouseId), {
      store_id: storeId,
      location_data: {
        apply_low_stock_threshold_to_all_locations: threshold,
      },
    })
  },

  // ---- Per-provider connect/disconnect (mirrors legacy useIntegrations.js) ----

  connectHomerun: async (
    storeId: string,
    data: { homerunStoreId: string },
  ): Promise<void> => {
    await httpClient.json.post('/stores/homerun', {
      store_id: storeId,
      homerun_store_id: data.homerunStoreId,
      connection: true,
      bs_warehouse_id_to_homerun_location_id: {},
    })
  },

  disconnectHomerun: async (storeId: string): Promise<void> => {
    await httpClient.json.put('/stores/homerun', {
      store_id: storeId,
      connection: false,
    })
  },

  connectOnfleet: async (
    storeId: string,
    data: {
      apiKey: string
      webhookSecret: string
      createPickupTask: boolean
    },
  ): Promise<void> => {
    await httpClient.json.post('/stores', {
      action: 'update_of_keys',
      store_id: storeId,
      parameters: {
        storeId,
        enable_of: true,
        of_api_key: data.apiKey,
        of_webhooks_secret: data.webhookSecret,
        create_onfleet_pickup_task: data.createPickupTask,
      },
    })
  },

  disconnectOnfleet: async (storeId: string): Promise<void> => {
    await httpClient.json.post('/stores', {
      action: 'update_of_keys',
      store_id: storeId,
      parameters: {
        storeId,
        enable_of: false,
      },
    })
  },

  connectTwoPoint: async (
    storeId: string,
    data: { apiKey: string },
  ): Promise<void> => {
    await httpClient.json.post('/stores/tpd', {
      store_id: storeId,
      connection: true,
      tpd_api_key: data.apiKey,
    })
  },

  disconnectTwoPoint: async (storeId: string): Promise<void> => {
    await httpClient.json.put('/stores/tpd', {
      store_id: storeId,
      connection: false,
    })
  },

  connectCanfleet: async (storeId: string): Promise<void> => {
    await httpClient.json.post(`/stores/${storeId}/canfleet/integration`, {
      store_id: storeId,
    })
  },

  connectCanadapost: async (
    storeId: string,
    data: {
      username: string
      password: string
      customerNumber: string
      contractId?: string
      paymentType?: 'Account' | 'CreditCard'
    },
  ): Promise<void> => {
    await httpClient.json.post('/stores/canadapost-integration', {
      store_id: storeId,
      canada_post_enabled: true,
      canada_post_username: data.username,
      canada_post_password: data.password,
      canada_post_customer_number: data.customerNumber,
      canada_post_contract_id: data.contractId ?? '',
      canada_post_intended_method_of_payment: data.paymentType ?? 'CreditCard',
    })
  },

  disconnectCanadapost: async (storeId: string): Promise<void> => {
    await httpClient.json.delete('/stores/canadapost-integration', {
      data: { store_id: storeId },
    })
  },

  previewTimeSlots: async (
    warehouseId: string,
    body: PreviewTimeSlotRequest,
  ): Promise<PreviewTimeSlotResponse> => {
    return (await httpClient.json.post(
      `/warehouses/location_v2/${warehouseId}/get_time_slot`,
      body,
    )) as PreviewTimeSlotResponse
  },

  // ---- Warehouse zones (delivery zones) ----
  // All `/warehouse_zones` routes pivot on `warehouse_id`; the action verb is
  // sent in the body. Mirrors legacy `DeliveryV2/DeliveryZone/hooks.js`.

  listAllZones: async (warehouseId: string): Promise<WarehouseZoneApi[]> => {
    if (!warehouseId) return []
    const raw = (await httpClient.json.post('/warehouse_zones', {
      action: 'list_all',
      warehouse_id: warehouseId,
      parameters: {},
    })) as WarehouseZoneApi[] | null
    return raw ?? []
  },

  listZones: async (params: WhZoneListParams): Promise<WhZoneListResponse> => {
    const search = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') search.set(k, String(v))
    }
    const raw = (await httpClient.json.get(
      `/warehouse_zones/list?${search.toString()}`,
    )) as WhZoneListResponse | null
    return raw ?? { items: [] }
  },

  createZone: async (
    warehouseId: string,
    zone: WarehouseZoneApi,
  ): Promise<WarehouseZoneApi> => {
    // Strip the placeholder `_id: ''` legacy adapters leave on new records.
    const { _id, ...parameters } = zone
    return (await httpClient.json.post('/warehouse_zones', {
      action: 'create',
      warehouse_id: warehouseId,
      parameters: _id ? { _id, ...parameters } : parameters,
    })) as WarehouseZoneApi
  },

  updateZone: async (
    warehouseId: string,
    zone: WarehouseZoneApi,
  ): Promise<WarehouseZoneApi> => {
    return (await httpClient.json.post('/warehouse_zones', {
      action: 'update',
      warehouse_id: warehouseId,
      parameters: zone,
    })) as WarehouseZoneApi
  },

  deleteZone: async (warehouseId: string, zoneId: string): Promise<void> => {
    await httpClient.json.post('/warehouse_zones', {
      action: 'delete',
      warehouse_id: warehouseId,
      parameters: { _id: zoneId },
    })
  },

  pasteZones: async (params: {
    warehouseId: string
    fromWarehouseId: string
    areaType: ZoneAreaType
  }): Promise<void> => {
    await httpClient.json.post('/warehouse_zones/paste', {
      warehouse_id: params.warehouseId,
      area_type: params.areaType,
      from_warehouse_id: params.fromWarehouseId,
    })
  },

  importZones: async (formData: FormData): Promise<void> => {
    // Multipart — use the form-data client, not `.json`.
    await httpClient.post('/warehouse_zones/import', formData)
  },

  findPosition: async (
    address: FindPositionRequest,
  ): Promise<FindPositionResponse> => {
    return (await httpClient.json.post(
      '/warehouses/find-position',
      address,
    )) as FindPositionResponse
  },
}
